<template>
    <div :class="['wishlist-details--wrapper', breakpoint]">
        <div v-if="loading && wishlist.items_count > 0" class="skeleton-loading">
            <b-skeleton height="100px"></b-skeleton>
            <b-skeleton height="100px"></b-skeleton>
        </div>
        <div class="wishlist-details" v-else-if="wishlist.items_count > 0">
            <b-button-group v-if="notPublic" class="wishlist--action-buttons">
                <b-button
                    size="sm"
                    variant="outline-primary"
                    v-b-modal="wishlist.uuid"
                >
                    {{ $t('wishlist.editListButton') }}
                    <b-icon-pencil class="ml-1"></b-icon-pencil>
                </b-button>
                <b-button
                    size="sm"
                    variant="outline-primary"
                    @click="share(wishlist.id)"
                >
                    {{ $t('wishlist.shareListButton') }}
                    <b-icon-share class="ml-1"></b-icon-share>
                </b-button>
            </b-button-group>
            <section v-if="isPublic" class="wishlist-top-public">
                <add-to-cart
                    v-if="wishlist.type !== 'wedding'"
                    class="add-list-to-cart"
                    :block="isMobile"
                    :products="items"
                ></add-to-cart>
            </section>
            <section v-if="!loading">
                <edit-wishlist
                    :wishlist="wishlist"
                    @wishlist-deleted="handleWishlistDeleted"
                    @wishlist-updated="() => refresh()"
                ></edit-wishlist>
                <article class="wishlist-item" v-for="(item) in items" :key="item.product_id">
                    <div class="wishlist-item-grid" v-if="item.product">
                        <lazy-image class="wishlist-item-grid__image" :src="imageSource(item)"/>
                        <div class="wishlist-item-grid__product">
                            <div class="wishlist-item-grid__product--header">
                                <div>
                                    <div class="manufacturer">
                                        {{ item?.product?.manufacturer?.translated?.name }}
                                    </div>
                                    <a :href="extractLink(item)">{{ item?.product?.name }}</a>
                                </div>
                                <align-right class="remove-button">
                                    <b-button
                                        v-if="notPublic"
                                        :aria-label="$t('wishlist.removeProductButton')"
                                        @click="deleteProduct(item)"
                                    >
                                        <b-icon-x-circle-fill v-if="!deleteLoading[item.id]"/>
                                        <b-spinner v-else small/>
                                    </b-button>
                                </align-right>
                            </div>
                            <section class="product-details">
                                <price :product="item.product"></price>
                                <stock :product="item.product"></stock>
                            </section>
                            <section class="tools">
                                <div class="quantity">
                                    <div class="qty-input" v-if="notPublic">
                                        <b-button @click="decrementItem(item)" variant="light">-</b-button>
                                        <b-input
                                            v-model.lazy="item.qty"
                                            debounce="300"
                                            @change="updateProduct(item)"
                                        ></b-input>
                                        <b-button @click="incrementItem(item)" variant="light">+</b-button>
                                    </div>
                                </div>
                                <align-right v-if="!isMobile">
                                    <add-purchase-item
                                        class="mt-2"
                                        v-if="isPublic"
                                        :is-public="isPublic"
                                        :wishlist="wishlist"
                                        :item="item"
                                        @update:items="updateItemsFromResponse"
                                    />
                                    <add-to-cart v-else :products="[item]"/>
                                </align-right>
                            </section>
                        </div>
                    </div>
                    <div v-else class="wishlist-item-grid">
                        <lazy-image class="wishlist-item-grid__image" :src="imageSource(item)"/>
                        <div class="wishlist-item-grid__product">
                            <div class="wishlist-item-grid__product--header">
                                <span class="missing-product">{{ $t('wishlist.missingProduct') }}</span>
                                <align-right>
                                    <b-button
                                        v-if="notPublic"
                                        :aria-label="$t('wishlist.removeProductButton')"
                                    >
                                        <b-icon-x-circle-fill/>
                                    </b-button>
                                </align-right>
                            </div>
                        </div>
                    </div>
                    <div v-if="isMobile" class="pt-3">
                        <add-purchase-item
                            v-if="isPublic"
                            :is-public="isPublic"
                            :wishlist="wishlist"
                            :item="item"
                            @update:items="updateItemsFromResponse"
                        />
                        <add-to-cart v-else :products="[item]"/>
                    </div>
                </article>
                <div v-if="items.length > 1" class="wishlist-bottom-actions">
                    <slot name="wishlist-bottom-actions">
                        <b-button
                            v-if="notPublic"
                            variant="primary"
                            :href="shopLink"
                        >
                            {{ $t('wishlist.goToStore') }}
                        </b-button>
                        <add-to-cart
                            v-if="wishlist.type !== 'wedding'"
                            class="add-list-to-cart"
                            :block="isMobile"
                            :products="items"
                        ></add-to-cart>
                    </slot>
                </div>
            </section>
        </div>
        <div class="wishlist-details" v-else>
            <b-alert show variant="info">
                {{ $t('wishlist.emptyItems') }}
                <b-button class="p-0" v-if="notPublic && !listIsNew" @click="removeList(wishlist.id)">
                    {{ $t('wishlist.removeListButton') }}?
                </b-button>
            </b-alert>
            <slot name="empty-button">
                <b-button v-if="notPublic" variant="primary" :href="shopLink">
                    {{ $t('wishlist.goToStore') }}
                </b-button>
            </slot>
        </div>
        <b-modal
            id="share-wishlist"
            v-model="showModal"
            :ok-title="$t('wishlist.closeShare')"
            :title="$t('wishlist.shareTitle')"
            :cancel-disabled="true"
        >
            <b-form-group :label="$t('wishlist.share.linkLabel')">
                <div class="share-list-copy">
                    <b-input-group>
                        <b-input :value="shareLink" readonly></b-input>
                        <b-button variant="outline-primary" @click="copyShareLink">{{
                                $t('wishlist.copyButton')
                            }}
                        </b-button>
                    </b-input-group>
                </div>
            </b-form-group>

            <b-form-group :label="$t('wishlist.edit.type')">
                <div v-if="wishlist.type !== 'wedding'">
                    <v-select
                        v-model="wishlist.type"
                        :reduce="(opt) => opt.id"
                        :options="typeThemes"
                        :clearable="false"
                        @option:selected="updateWishlistType(wishlist, $event)"
                    ></v-select>
                </div>
                <div v-else>
                    {{ $t(`wishlist.typeTheme.${wishlist.type}`) }}
                </div>
            </b-form-group>

            <b-form-group :label="$t('wishlist.share.emailLabel')">
                <div class="share-list-email">
                    <b-input v-model="shareLinkState.email"
                             :placeholder="$t('wishlist.share.emailPlaceholder')"></b-input>
                </div>
            </b-form-group>
            <b-form-group :label="$t('wishlist.share.emailMessage')">
                <div class="share-list-email">
                    <b-textarea v-model="shareLinkState.message"></b-textarea>
                    <b-button variant="primary" @click="sendShareEmail">
                        {{ $t('wishlist.share.emailButton') }}
                    </b-button>
                </div>
            </b-form-group>
        </b-modal>
    </div>
</template>
<script>
import { defineComponent, unref } from 'vue';
import { useWishlistStore } from '@stores/Wishlist';
import LazyImage from '@components/Lazy/LazyImage.vue';
import { useAppStore } from '@stores/App';
import AlignRight from '@components/Layout/AlignRight.vue';
import { BModal } from 'bootstrap-vue';
import AddToCart from '@components/Wishlist/AddToCart.vue';
import AddPurchaseItem from '@components/Wishlist/AddPurchaseItem.vue';
import EditWishlist from '@components/Wishlist/EditWishlist.vue';
import Price from '@components/Wishlist/Shopware/Price.vue';
import Stock from '@components/Wishlist/Shopware/Stock.vue';

export default defineComponent({
    components: { Stock, Price, EditWishlist, AddPurchaseItem, AddToCart, BModal, AlignRight, LazyImage },
    props: {
        isPublic: {
            type: Boolean,
            default() {
                return false;
            }
        },
        wishlist: {
            type: Object,
            default() {
                return null;
            },
        },
        refresh: {
            type: Function,
            default() {
                return () => {
                };
            }
        }
    },
    mounted() {
        if (!this.isPublic) {
            this.loading = true;
            const store = useWishlistStore();
            store.getListItems(this.wishlist.id).then((list) => {
                this.items = list.items;
                this.loading = false;
            }).finally(() => {
                this.loading = false;
            });
        }
    },
    watch: {
        wishlist: {
            immediate: true,
            deep: true,
            handler(val) {
                if (val?.items?.length >= 0) {
                    this.items = val.items;
                }
            },
        },
    },
    computed: {
        listIsNew() {
            const now = new Date();
            const diff = now - (new Date(this.wishlist.created_at));
            const threshold = 1000 * 60 * 2;
            return diff < threshold;
        },
        breakpoint() {
            const app = useAppStore();
            const active = app.getBreakpoints().active();
            return unref(active);
        },
        notPublic() {
            return !this.isPublic;
        },
        shareLink() {
            return `https://${location.host}/wishlist/${this.wishlist.uuid}`;
        },
        isMobile() {
            const app = useAppStore();
            const active = app.getBreakpoints().active();
            return [ 'sm' ].includes(active.value);
        },
        shopLink() {
            const app = useAppStore();

            if (this.wishlist.type === 'christmas') {
                return app.formatLink('/create-christmas-list');
            }

            return app.formatLink('/create-wishlist');
        },
    },
    data() {
        const wishlists = useWishlistStore();
        const app = useAppStore();
        const $t = this.$t;

        return {
            basePath: app.basePath,
            items: [],
            loading: false,
            shareLinkState: {
                email: null,
                message: null,
                type: this.wishlist.type,
            },
            typeThemes: wishlists.getWishlistTypeThemes().map((item) => {
                item.label = this.$t(item.label);
                return item;
            }),
            fields: [
                {
                    key: 'product',
                    label: $t('wishlist.field.product'),
                },
            ],
            isDevelopment: app.isDevelopment(),
            showModal: false,
            app: app,
            deleteLoading: {},
        };
    },
    methods: {
        extractLink(item) {
            const basePath = this.basePath;
            const seo = item?.product?.seo_urls[0] || null;

            if (!seo) {
                return null;
            }

            return basePath + (seo.seo_path_info || seo.path_info);
        },
        imageSource(item) {
            const id = item.product_id;
            const link = item?.product?.cover?.media?.url;

            if (!link) {
                return `https://my.newport.se/images/product/${id}/main/square-200`;
            }

            return link;
        },
        share() {
            this.showModal = !this.showModal;
            this.togglePublic(true);
        },
        async togglePublic(value) {
            const store = useWishlistStore();
            await store.updateList(this.wishlist.id, {
                public: value,
            });
        },
        handleWishlistDeleted(id) {
            this.refresh();
            this.$emit('wishlist-deleted', id);
        },
        async removeList(id) {
            const store = useWishlistStore();
            try {
                await store.deleteList(id);
                this.handleWishlistDeleted(id);
            } catch (e) {
                console.log(e);
            }
        },
        copyShareLink() {
            navigator.clipboard.writeText(this.shareLink);
            this.togglePublic(true);
        },
        decrementItem(item) {
            if (item.qty === 1) {
                this.deleteProduct(item.product_id);
            }

            if (item.qty > 1) {
                item.qty = parseInt(item.qty) - 1;
            }
            this.updateProduct(item);
        },
        incrementItem(item) {
            item.qty = parseInt(item.qty) + 1;
            this.updateProduct(item);
        },
        async updateProduct(item) {
            const store = useWishlistStore();
            const response = await store.updateProduct(this.wishlist.id, item.product_id, item.qty);
            this.updateItemsFromResponse(response.data.items, item);
        },
        async deleteProduct(item) {
            this.deleteLoading[item.id] = true;
            const store = useWishlistStore();
            const response = await store.deleteProduct(this.wishlist.id, item.product_id);

            this.items = this.items.filter(current => current.id !== item.id);
            this.updateItemsFromResponse(response.data.items, item);
            this.deleteLoading[item.id] = false;
        },
        updateItemsFromResponse(newItems, item) {
            if (item) {
                const oldItem = this.items.find((oldItem) => oldItem.id === item.id);
                oldItem.qty = item.qty;
                return;
            }

            this.items = newItems.map((item) => {
                const oldItem = this.items.find((it) => it.id === item.id);
                const product = oldItem.product;

                if (oldItem) {
                    return {
                        ...oldItem,
                        ...item,
                        product: product,
                    };
                }

                return item;
            });
        },
        async updateWishlistType(wishlist, event) {
            try {
                const store = useWishlistStore();
                await store.updateList(wishlist.id, {
                    type: event.id,
                });
            } catch (e) {
                throw e;
            }
        },
        async sendShareEmail() {
            const store = useWishlistStore();

            try {
                await store.sendShareEmail(this.wishlist.id, this.shareLinkState.email, this.shareLinkState.message);
                this.shareLinkState.email = null;
                this.$bvToast.toast('Success', { title: 'Success', variant: 'success' });
            } catch (e) {
                this.$bvToast.toast('Failed', { title: 'Failed', variant: 'danger' });
            }
        },
    },
});
</script>
<style lang="scss" scoped>
.wishlist-details {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    section {
        width: 100%;
    }
}

::v-deep(thead) {
    display: none !important;
}

.manufacturer {
    font-family: Chronicle, serif;
    font-size: 11px;
    font-weight: 400;
    line-height: 1.5;
    text-transform: uppercase;
    -webkit-font-smoothing: auto;
}

.quantity {
    display: grid;
    grid-template-columns: max-content max-content;
    gap: 1rem;
    align-items: center;
}

.qty-input {
    display: grid;
    grid-template-columns: 48px 48px 48px;

    ::v-deep(.btn) {
        position: relative;
        z-index: 1;
        width: 48px !important;
        height: 40px !important;
        padding: 0 !important;
        background: #f3f3f5 !important;
    }

    ::v-deep(input) {
        position: relative;
        z-index: 2;
        text-align: center;
        width: 100% !important;
        height: 40px !important;
        padding: 0 !important;
        background: #f3f3f5 !important;
        border-color: #f3f3f5 !important;
    }
}

.price {
    font-weight: 500;
    letter-spacing: 0;
}

.tools {
    display: grid;
    grid-template-columns: 1fr min-content;
    align-items: end;
}

.share-list-copy {
    display: grid;
}

.share-list-email {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    .btn {
        width: max-content;
    }
}

.add-list-to-cart {
    padding: 0;
    margin-left: auto;
}

.skeleton-loading {
    display: grid;
    gap: 1rem;
    grid-template-columns: 128px 1fr;
}

.remove-button {
    .btn-secondary {
        height: min-content;
        padding: 0;
    }
}

.wishlist-actions .btn {
    border-radius: 0;
    margin-bottom: 0.5rem;
}

.wishlist-item {
    margin-bottom: 1rem;
    border-bottom: 1px solid #f3f3f5;
    padding-bottom: 1rem;

    &:first-child {
        border-top: 1px solid #f3f3f5;
        padding-top: 1rem;
    }
}

.wishlist-item-grid {
    display: grid;
    gap: 1rem;
    grid-template-columns: 128px 1fr;

    .sm & {
        grid-template-columns: 80px 1fr;
    }

    &__product {
        display: grid;

        &--header {
            display: grid;
            grid-template-columns: minmax(min-content, max-content) 1fr;
            gap: 1rem;
        }
    }

    &__image {
        max-width: 128px;
        height: max-content;
    }

    &__image {
        position: relative;
        mix-blend-mode: multiply;
    }

    &__image:after {
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        content: "";
        position: absolute;
        box-shadow: inset 0 0 90px 8px rgb(240, 240, 240, 0.75);
        mix-blend-mode: multiply;
        pointer-events: none;
    }
}

.missing-product {
    color: #a8a8a8;
    text-decoration: line-through;
}

.wishlist--action-buttons {
    margin-left: auto;
    margin-bottom: 1rem;

    .sm & {
        width: 100%;
    }
}

.wishlist-bottom-actions {
    display: flex;
    justify-content: space-between;
    width: 100%;

    .sm & {
        flex-direction: column;
        gap: 16px;
    }
}

.wishlist-top-public {
    display: flex;
    justify-content: flex-end;
    padding-bottom: 1rem;

    .sm & {
        display: block;
    }
}

.product-details {
    display: flex;
    margin-top: 4px;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 1rem;
}

::v-global(.main-account-wedding-list .add-to-cart-form) {
    display: none;
}

:deep(.wishlist-details) td {
    vertical-align: middle;
}
</style>
